import {
  type OldProductDTO,
  type OrderDTO,
  type OrderItemDTO,
  type PostNordAddress,
  type PostNordCheckoutDeliveryOptionSelected,
  type PostNordConsignorParty,
  type PostNordCountry,
  type PostNordEdiInstructionRequest,
  type PostNordParty,
  type PostNordWarehouse,
  type ShippingProviderConfig
} from 'ecosystem'
import { v4 as uuid } from 'uuid'
import { calculateOrderTotalWeight, roundNumber } from 'shared-utils'
import type { OrderProvisionResult } from '../types'
import { postnordApi } from '../../../../utils/postnord'
import * as productsApi from '../../../productsEndpoint'
import { alertService } from '../../../../utils/alert-service'

export const getPostNordStreetsFromAddress = (address: Partial<PostNordAddress>): string[] => {
  const street =
    address.street ||
    (address.streetName ? `${address.streetName} ${address.streetNumber ?? ''}` : '')
  return [street.trim()]
}

const generatePostnordEdiBookingRequestPayload = ({
  applicationId,
  deliveryOption,
  postnordCustomerId,
  warehouse,
  companyName,
  recipientParty,
  products,
  orderItems
}: {
  applicationId: PostNordEdiInstructionRequest['application']['applicationId']
  deliveryOption: PostNordCheckoutDeliveryOptionSelected
  postnordCustomerId: string
  companyName: string
  warehouse: PostNordWarehouse
  recipientParty: PostNordParty
  products: OldProductDTO[]
  orderItems: OrderItemDTO[]
}): PostNordEdiInstructionRequest => {
  const getConsignorIssuerCode = () => {
    const dict: Record<PostNordCountry, PostNordConsignorParty['issuerCode']> = {
      DK: 'Z11',
      SE: 'Z12',
      NO: 'Z13',
      FI: 'Z14'
    }
    return dict[(warehouse.address.countryCode ?? 'SE') as PostNordCountry]
  }
  // Postnord requires weight > 0
  const { totalWeight } = calculateOrderTotalWeight(orderItems, products)
  const parcelTotalWeight = roundNumber(totalWeight, 3) || 1

  return {
    messageDate: new Date().toISOString(),
    messageFunction: 'Instruction',
    messageId: uuid().replace(/-/g, '').slice(0, 14),
    application: {
      applicationId,
      name: 'Granditude',
      version: '1.0'
    },
    updateIndicator: 'Original',
    shipment: [
      {
        shipmentIdentification: {
          shipmentId: '0'
        },
        service: {
          basicServiceCode: deliveryOption.details.bookingInstructions.serviceCode,
          additionalServiceCode: deliveryOption.details.bookingInstructions.additionalServiceCodes
        },
        numberOfPackages: {
          value: 1
        },
        totalGrossWeight: {
          value: parcelTotalWeight,
          unit: 'KGM'
        },
        parties: {
          consignor: {
            issuerCode: getConsignorIssuerCode(),
            partyIdentification: {
              partyId: postnordCustomerId,
              partyIdType: '160'
            },
            party: {
              nameIdentification: {
                companyName
              },
              address: {
                streets: getPostNordStreetsFromAddress(warehouse.address),
                postalCode: warehouse.address.postCode,
                city: warehouse.address.city,
                countryCode: warehouse.address.countryCode as PostNordCountry
              }
            }
          },
          consignee: recipientParty,
          // Only service points or parcel lockers will have location
          ...(deliveryOption.details.location
            ? {
                deliveryParty: {
                  partyIdentification: {
                    partyId: deliveryOption.details.bookingInstructions.servicePointId ?? '',
                    partyIdType: '156'
                  },
                  party: {
                    nameIdentification: {
                      name: deliveryOption.details.location.name
                    },
                    address: {
                      streets: getPostNordStreetsFromAddress(
                        deliveryOption.details.location.address
                      ),
                      postalCode: deliveryOption.details.location.address.postCode,
                      city: deliveryOption.details.location.address.city,
                      countryCode: deliveryOption.details.location.address
                        .countryCode as PostNordCountry
                    }
                  }
                }
              }
            : {})
        },
        goodsItem: [
          {
            packageTypeCode: 'PC',
            items: [
              {
                itemIdentification: {
                  itemId: '0',
                  itemIdType: 'SSCC'
                },
                grossWeight: {
                  value: parcelTotalWeight,
                  unit: 'KGM'
                }
              }
            ]
          }
        ]
      }
    ]
  } satisfies PostNordEdiInstructionRequest
}

export const generatePostnordShippingProviderResult = async (
  orderData: OrderDTO,
  contactInfo: Required<PostNordParty['party']>['contact'],
  shippingProviderConfig?: ShippingProviderConfig
): Promise<OrderProvisionResult['shippingProviderResult']> => {
  const postnordDetails = orderData.shippingInfo?.additionalInfo?.postnordDetails

  if (!postnordDetails) {
    void alertService.send('Provision order | Get postnord data from order', {
      blocks: [
        {
          title: 'Missing data in',
          content: 'orderData.shippingInfo?.additionalInfo?.postnordDetails'
        },
        {
          title: 'Order',
          content: orderData
        },
        {
          title: 'Error',
          content: 'No postnord shipping data'
        }
      ]
    })
    throw Error('No postnord shipping data')
  }

  if (postnordDetails?.isPickupFromStore) {
    return {
      providerName: orderData.shippingProvider!,
      postnordResponse: postnordDetails
    } satisfies OrderProvisionResult['shippingProviderResult']
  }

  // Check if postnord booking is created already
  if (postnordDetails?.bookingDetails?.bookingId) {
    return {
      providerName: orderData.shippingProvider!,
      postnordResponse: postnordDetails
    } satisfies OrderProvisionResult['shippingProviderResult']
  }

  if (
    !postnordDetails?.deliveryOption ||
    !postnordDetails?.recipientAddress ||
    !shippingProviderConfig?.postnordOptions
  ) {
    void alertService.send('Provision order | Prepare data for Postnord booking creation', {
      blocks: [
        {
          title: 'Missing data in one of the properties',
          content: [
            'postnordDetails?.deliveryOption',
            'postnordDetails?.recipientAddress',
            'shippingProviderConfig?.postnordOptions'
          ]
        },
        {
          title: 'Order',
          content: orderData
        },
        {
          title: 'Shipping Provider Config',
          content: shippingProviderConfig
        },
        {
          title: 'Error',
          content: 'No postnord shipping details for booking creation'
        }
      ]
    })

    throw Error('No postnord shipping details for booking creation')
  }

  const fullProducts: OldProductDTO[] = await (async () => {
    const productIds = orderData.items?.map((item) => item.product.id) ?? []

    if (!productIds?.length) {
      return []
    }

    const productsResponse = await productsApi.listProductsFull(
      {
        productIds
      },
      {
        size: productIds.length
      }
    )

    return productsResponse.content
  })()

  if (fullProducts.length !== orderData.items?.length) {
    void alertService.send(
      'Provision order | Prepare data for Postnord booking creation | Get full products',
      {
        blocks: [
          {
            title: 'Full Products',
            content: fullProducts
          },
          {
            title: 'Order',
            content: orderData
          },
          {
            title: 'Error',
            content: 'Products are not found'
          }
        ]
      }
    )
    throw new Error('Products are not found')
  }

  const bookingRequestPayload = generatePostnordEdiBookingRequestPayload({
    applicationId: shippingProviderConfig.postnordOptions.applicationId,
    deliveryOption: postnordDetails.deliveryOption,
    postnordCustomerId: shippingProviderConfig.postnordOptions.customerKey,
    warehouse: shippingProviderConfig.postnordOptions.warehouses[0],
    companyName: shippingProviderConfig.postnordOptions.companyName ?? '',
    recipientParty: {
      party: {
        nameIdentification: {
          name: contactInfo.contactName
        },
        address: {
          streets: getPostNordStreetsFromAddress(postnordDetails.recipientAddress),
          postalCode: postnordDetails.recipientAddress.postCode,
          countryCode: postnordDetails.recipientAddress.countryCode as PostNordCountry,
          city: postnordDetails.recipientAddress.city
        },
        contact: contactInfo
      }
    },
    products: fullProducts,
    orderItems: orderData.items
  })

  // Book postnord shipment
  const { data: postnordResponseData, error: postnordResponseError } =
    await postnordApi.bookShipmentEdiLabelsPdf(bookingRequestPayload)

  if (
    postnordResponseError ||
    !postnordResponseData ||
    postnordResponseData.bookingResponse.idInformation[0]?.status?.toLowerCase() !== 'ok'
  ) {
    void alertService.send('Provision order | Create Postnord booking', {
      blocks: [
        {
          title: 'Payload',
          content: bookingRequestPayload
        },
        {
          title: 'Order',
          content: orderData
        },
        {
          title: 'Response data',
          content: postnordResponseData
        },
        {
          title: 'Error',
          content: postnordResponseError
        }
      ]
    })

    throw Error(postnordResponseError || 'PostNord booking shipment error')
  }

  return {
    providerName: orderData.shippingProvider!,
    postnordResponse: {
      deliveryOption: postnordDetails.deliveryOption,
      bookingDetails: {
        bookingId: postnordResponseData.bookingResponse.bookingId,
        trackingUrl: postnordResponseData.bookingResponse.idInformation[0].urls.find(
          (item) => item.type === 'TRACKING'
        )?.url,
        trackingNumber: postnordResponseData.bookingResponse.idInformation[0].ids[0]?.value,
        labelUrl: postnordResponseData.labelPrintout[0].printout.uriStoreLabel
      },
      recipientAddress: postnordDetails.recipientAddress
    }
  } satisfies OrderProvisionResult['shippingProviderResult']
}
